import {createSlice, PayloadAction} from "@reduxjs/toolkit";

/**
 * Selectable app templates.
 *
 * 'ims'   -> the original IMS v2 layout (src/components/layout)
 * 'skote' -> the added Skote-style vertical layout (src/templates/skote)
 */
export const TEMPLATES = {
    IMS: 'ims',
    SKOTE: 'skote',
} as const;

export type TemplateId = (typeof TEMPLATES)[keyof typeof TEMPLATES];

/** Skote sidebar looks (light-only variants, mirroring Skote's customizer). */
export const SKOTE_SIDEBAR_THEMES = {
    DARK: 'dark',
    LIGHT: 'light',
    COLORED: 'colored',
    WINTER: 'winter',
    LADYLIP: 'ladylip',
    PLUMPLATE: 'plumplate',
    STRONGBLISS: 'strongbliss',
    GREATWHALE: 'greatwhale',
} as const;

export type SkoteSidebarTheme = (typeof SKOTE_SIDEBAR_THEMES)[keyof typeof SKOTE_SIDEBAR_THEMES];

/** Topbar look inside the Skote template. */
export const SKOTE_TOPBAR_THEMES = {
    LIGHT: 'light',
    COLORED: 'colored',
} as const;

export type SkoteTopbarTheme = (typeof SKOTE_TOPBAR_THEMES)[keyof typeof SKOTE_TOPBAR_THEMES];

/** Content width inside the Skote template. */
export const SKOTE_WIDTH_TYPES = {
    FLUID: 'fluid',
    BOXED: 'boxed',
} as const;

export type SkoteWidthType = (typeof SKOTE_WIDTH_TYPES)[keyof typeof SKOTE_WIDTH_TYPES];

/** How the navigation menu is presented to the end-user. */
export const MENU_LAYOUTS = {
    VERTICAL: 'vertical',
    HORIZONTAL: 'horizontal',
} as const;

export type MenuLayout = (typeof MENU_LAYOUTS)[keyof typeof MENU_LAYOUTS];

export interface SkoteOptions {
    sidebar: SkoteSidebarTheme;
    topbar: SkoteTopbarTheme;
    width: SkoteWidthType;
    /** Initial sidebar state (collapsed icon rail) whenever the Skote layout mounts. */
    defaultCollapsed: boolean;
    /** Navigation display: vertical sidebar or horizontal top menu bar. */
    menuLayout: MenuLayout;
}

const defaultSkote: SkoteOptions = {
    sidebar: SKOTE_SIDEBAR_THEMES.LIGHT,
    topbar: SKOTE_TOPBAR_THEMES.LIGHT,
    width: SKOTE_WIDTH_TYPES.FLUID,
    defaultCollapsed: false,
    menuLayout: MENU_LAYOUTS.VERTICAL,
};

/**
 * Template resolution:
 *  - `userOverride`  = the template an individual user explicitly picked
 *                      (null => "follow the school default").
 *  - `schoolDefault` = the template applied by default for the school
 *                      (single school per deployment).
 *
 * NOTE ON PERSISTENCE: the school default is currently stored client-side
 * (redux-persist). The pure functions below are the seam: when a server-side
 * school setting endpoint is added, only `loadSchoolDefault`/`saveSchoolDefault`
 * need to be pointed at it — the resolution logic stays identical.
 */
export const resolveEffectiveTemplate = (
    userOverride: TemplateId | null,
    schoolDefault: TemplateId
): TemplateId => userOverride ?? schoolDefault;

export interface TemplateState {
    userOverride: TemplateId | null;
    schoolDefault: TemplateId;
    skote: SkoteOptions;
}

const initialState: TemplateState = {
    userOverride: null,
    // Skote is the primary/default template; users can still switch back to the old IMS layout.
    schoolDefault: TEMPLATES.SKOTE,
    skote: {...defaultSkote},
};

/**
 * Migrates the legacy persisted shape ({ current: 'ims'|'skote' }) to the new
 * userOverride/schoolDefault shape. Legacy `current` represented an *explicit*
 * template selection, so it maps to userOverride (null when it was the default
 * 'ims').
 */
export const normalizeLegacyTemplateState = (persisted: any): TemplateState => {
    if (persisted == null || typeof persisted !== 'object') {
        return {...initialState};
    }
    const hasNewShape =
        Object.prototype.hasOwnProperty.call(persisted, 'userOverride') &&
        Object.prototype.hasOwnProperty.call(persisted, 'schoolDefault');
    if (hasNewShape) {
        return persisted;
    }
    const legacyCurrent = persisted.current === TEMPLATES.SKOTE ? TEMPLATES.SKOTE : null;
    return {
        userOverride: legacyCurrent,
        schoolDefault: TEMPLATES.IMS,
        skote: {...defaultSkote, ...(persisted.skote ?? {})},
    };
};

/** Persist migration used by redux-persist (store version 2). */
export const migrateTemplateState = (state: any): any => {
    if (state == null || state.template == null) {
        return state;
    }
    return {
        ...state,
        template: normalizeLegacyTemplateState(state.template),
    };
};

/**
 * v3 migration: the Skote template is now locked to the light look (light
 * sidebar + light topbar). Any stored value that isn't light (dark, colored,
 * gradients, colored topbar) is reset to the canonical light theme.
 */
export const migrateSkoteToLightDefault = (state: any): any => {
    const t = state?.template;
    const s = t?.skote;
    if (!s || (s.sidebar === SKOTE_SIDEBAR_THEMES.LIGHT && s.topbar === SKOTE_TOPBAR_THEMES.LIGHT)) {
        return state;
    }
    return {
        ...state,
        template: {
            ...t,
            skote: {
                ...s,
                sidebar: SKOTE_SIDEBAR_THEMES.LIGHT,
                topbar: SKOTE_TOPBAR_THEMES.LIGHT,
            },
        },
    };
};

/**
 * v4 migration: Skote becomes the school default template. Explicit per-user choices are kept —
 * those users can still switch back to the old IMS layout.
 */
export const migrateSchoolDefaultToSkote = (state: any): any => {
    const t = state?.template;
    if (!t || t.schoolDefault === TEMPLATES.SKOTE) {
        return state;
    }
    return {
        ...state,
        template: {...t, schoolDefault: TEMPLATES.SKOTE},
    };
};

/** Older persisted states may lack the nested `skote` options — normalize it. */
const ensureSkote = (state: TemplateState) => {
    if (!state.skote) {
        state.skote = {...defaultSkote};
    }
};

const templateSlice = createSlice({
    name: 'template',
    initialState,
    reducers: {
        /** Sets the personal override (explicit user choice). */
        setTemplate: (state, action: PayloadAction<TemplateId>) => {
            state.userOverride = action.payload;
        },
        /** Clears the personal override: from now on the school default applies. */
        clearUserTemplate: (state) => {
            state.userOverride = null;
        },
        /** Changes the template used by default for the school. */
        setSchoolDefault: (state, action: PayloadAction<TemplateId>) => {
            state.schoolDefault = action.payload;
        },
        setSkoteSidebar: (state, action: PayloadAction<SkoteSidebarTheme>) => {
            ensureSkote(state);
            state.skote.sidebar = action.payload;
        },
        setSkoteTopbar: (state, action: PayloadAction<SkoteTopbarTheme>) => {
            ensureSkote(state);
            state.skote.topbar = action.payload;
        },
        setSkoteWidth: (state, action: PayloadAction<SkoteWidthType>) => {
            ensureSkote(state);
            state.skote.width = action.payload;
        },
        setSkoteDefaultCollapsed: (state, action: PayloadAction<boolean>) => {
            ensureSkote(state);
            state.skote.defaultCollapsed = action.payload;
        },
        setMenuLayout: (state, action: PayloadAction<MenuLayout>) => {
            ensureSkote(state);
            state.skote.menuLayout = action.payload;
        },
    },
});

export const {
    setTemplate,
    clearUserTemplate,
    setSchoolDefault,
    setSkoteSidebar,
    setSkoteTopbar,
    setSkoteWidth,
    setSkoteDefaultCollapsed,
    setMenuLayout,
} = templateSlice.actions;

// ---- Selectors -------------------------------------------------------------

export const selectUserTemplate = (state: any): TemplateId | null =>
    state?.template?.userOverride ?? null;

export const selectSchoolDefault = (state: any): TemplateId =>
    state?.template?.schoolDefault ?? TEMPLATES.IMS;

export const selectEffectiveTemplate = (state: any): TemplateId =>
    resolveEffectiveTemplate(selectUserTemplate(state), selectSchoolDefault(state));

export const selectMenuLayout = (state: any): MenuLayout =>
    state?.template?.skote?.menuLayout ?? MENU_LAYOUTS.VERTICAL;

export default templateSlice.reducer;
